import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik, Field, Form, ErrorMessage } from "formik";
import * as jnb from "react-bootstrap";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { config } from "../../CommonUtils/CommonURLS";
import allowNumbersOnly, { allowAlphabetAndSpacesOnly, allowNumbersAndAlphabetsOnly } from "../../CommonUtils/CommonValidations";
import { emailValidation, ERROR_MSG, mobileValidation } from "../../CommonUtils/contextVariables";
import { CommonAxiosGet, CommonAxiosPost } from "../../CommonUtils/CommonAxios";
import { ImagePdfBucket } from "../../CommonUtils/ImagesBase64";
import { viewImage } from "../../CommonUtils/viewImage";
import { FaEye } from "react-icons/fa";
import { successAlert2 } from "../../CommonUtils/SweetAlerts";

export default function StudentDetailsForm({ refreshStatus, paymentFlag, studentRegNo }) {
  const [districts, setDistricts] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [transferColleges, setTransferColleges] = useState([]);
  const [isLoadingColleges, setIsLoadingColleges] = useState({ present: false, transfer: false });
  const [transferCollegeCourses, setTransferCollegeCourses] = useState([]);
  const [isLoadingTransferCourses, setIsLoadingTransferCourses] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [getstudentDtls, setStudentDetails] = useState({});
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [branches, setBranches] = useState([]);
  const userDtls = JSON.parse(sessionStorage.getItem("decodedToken")) || [];
  const username = userDtls?.empl_first_name || "";
  const userId = userDtls?.user_id || "";
  const mobileNo = userDtls?.mobile_no || "";
  const email = userDtls?.email || "";
  const gender = userDtls?.gender || "";
  const isStudent = userId.toLowerCase().startsWith("stut");
  const [presentCollegeStatus, setPresentCollegeStatus] = useState(null);
  const [isLoadingPresentCollegeStatus, setIsLoadingPresentCollegeStatus] = useState(false);
  const [isPharmaD, setIsPharmaD] = useState(false);
  // Calculate max date (18 years ago from today)
  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(today.getFullYear() - 17, today.getMonth(), today.getDate());
    return maxDate.toISOString().split("T")[0];
  };

  const initialValues = {
    studentRegNo: isStudent ? userId : studentRegNo || "",
    studentName: username || "",
    fatherName: "",
    motherName: "",
    dob: "",
    gender: gender || "",
    mobileNo: mobileNo || "",
    email: email || "",
    yearOfAdmission: "",
    ubCode: "",
    presentDistCode: "",
    presentCollCode: "",
    courseId: "",
    branch: "",
    presentAcYear: "",
    presentSemester: "",
    lateralEntry: false,
    transDistCode: "",
    transCollCode: "",
    transferReason: "",
    nextAcYear: "",
    nextSemester: "",
    sameSyllabus: "",
    currentSyllabusCopy: "",
    studentRollNo: "",
    presentInstType: "",
    transInstType: "",
  };

  const isAutonomousType = (value) => String(value || "").trim().toLowerCase() === "autonomous";
  const getTransferRuleStatus = (fromType, toType, presentAutonomousEligibility) => {
    const fromAutonomous = isAutonomousType(fromType);
    const toAutonomous = isAutonomousType(toType);

    if (fromAutonomous && !presentAutonomousEligibility.isEligible) {
      return {
        isAllowed: false,
        isPending: presentAutonomousEligibility.isPending,
        message: presentAutonomousEligibility.message,
      };
    }

    if (fromAutonomous) {
      return {
        isAllowed: true,
        isPending: false,
        message: "",
      };
    }

    if (toAutonomous) {
      return {
        isAllowed: false,
        isPending: false,
        message: "Non-Autonomous to Autonomous transfer is not allowed. Any transfer involving Autonomous institutes is not permitted.",
      };
    }

    return { isAllowed: true, isPending: false, message: "" };
  };

  const validationSchema = Yup.object().shape({
    studentRollNo: Yup.string().required(ERROR_MSG),
    presentInstType: Yup.string().required(ERROR_MSG),
    transInstType: Yup.string().required(ERROR_MSG),
    studentRegNo: Yup.string().required(ERROR_MSG),
    studentName: Yup.string().required(ERROR_MSG),
    fatherName: Yup.string().required(ERROR_MSG),
    motherName: Yup.string().required(ERROR_MSG),
    dob: Yup.string().required(ERROR_MSG),
    gender: Yup.string().required(ERROR_MSG),
    email: emailValidation,
    yearOfAdmission: Yup.string().required(ERROR_MSG),
    ubCode: Yup.string().required(ERROR_MSG),
    presentDistCode: Yup.string().required(ERROR_MSG),
    presentCollCode: Yup.string().required(ERROR_MSG),
    courseId: Yup.string().required(ERROR_MSG),
    branch: Yup.string().required(ERROR_MSG),
    presentAcYear: Yup.string().required(ERROR_MSG),
    presentSemester: Yup.string().when("courseId", {
      is: (courseId) => {
        const selectedCourse = courses.find((course) => String(course?.course_cat_id) === String(courseId));
        return selectedCourse?.course_cat_name !== "Pharma.D";
      },
      then: (schema) => schema.required(ERROR_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),
    mobileNo: mobileValidation,
    transDistCode: Yup.string().required(ERROR_MSG),
    transCollCode: Yup.string()
      .required(ERROR_MSG)
      .notOneOf([Yup.ref("presentCollCode")], "Transfer college cannot be the same as present college"),
    transferReason: Yup.string().required(ERROR_MSG),
    nextAcYear: Yup.string().required(ERROR_MSG),
    nextSemester: Yup.string().when("courseId", {
      is: (courseId) => {
        const selectedCourse = courses.find((course) => String(course?.course_cat_id) === String(courseId));
        return selectedCourse?.course_cat_name !== "Pharma.D";
      },
      then: (schema) => schema.required(ERROR_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),
    // lateralEntry: Yup.boolean().oneOf([true], ERROR_MSG),
    sameSyllabus: Yup.string().required(ERROR_MSG),
    currentSyllabusCopy: Yup.string().when("sameSyllabus", {
      is: "Yes",
      then: (schema) => schema.required(ERROR_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const studentdetailsformik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (!transferCourseAvailability.isAvailable) {
        Swal.fire({
          title: "Course Not Available",
          text: transferCourseAvailability.message,
          icon: "warning",
        });
        return;
      }

      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, submit it!",
        cancelButtonText: "No, cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          const url = `${config.url.STU_URL}saveStudentProfile`;
          CommonAxiosPost(url, values).then((res) => {
            if (res?.data?.scode === "01") {
              successAlert2(res?.data?.sdesc);
              getStudentProfile();
              refreshStatus();
            } else {
              Swal.fire({
                title: "Error",
                text: res?.data?.sdesc || res?.data?.ResponseDesc || "Submission failed",
                icon: "warning",
              });
            }
          });
        }
      });
    },
  });

  const normalizeGender = (value) => {
    const genderValue = String(value || "")
      .trim()
      .toUpperCase();
    if (genderValue === "M" || genderValue === "MALE") return "Male";
    if (genderValue === "F" || genderValue === "FEMALE") return "Female";
    if (genderValue === "O" || genderValue === "OTHER") return "Other";
    return "";
  };

  const isPharmaDName = (name) => String(name || "").trim().toLowerCase() === "pharma.d";

  const getNextAcademicProgress = (currentYear, currentSemester) => {
    const isLateralEntry = studentdetailsformik?.values?.lateralEntry || false;

    // Define progression based on lateral entry
    let progression;
    if (isLateralEntry) {
      // When lateral entry is checked, current eligibility starts from II Year SEM-2.
      // Keep IV Year SEM-2 in progression so next academic fields can still advance from IV SEM-1.
      progression = [
        ["II", "SEM-2"],
        ["III", "SEM-1"],
        ["III", "SEM-2"],
        ["IV", "SEM-1"],
        ["IV", "SEM-2"],
      ];
    } else {
      // When lateral entry is NOT checked - start from II Year
      progression = [
        ['I', 'SEM-2'],
        ["II", "SEM-1"],
        ["II", "SEM-2"],
        ["III", "SEM-1"],
        ["III", "SEM-2"],
        ["IV", "SEM-1"],
        ["IV", "SEM-2"],
      ];
    }

    const currentIndex = progression.findIndex(([year, semester]) => year === currentYear && semester === currentSemester);
    // Invalid input
    if (currentIndex === -1) {
      return { nextAcYear: "", nextSemester: "" };
    }
    // Already at last semester
    if (currentIndex === progression.length - 1) {
      return { nextAcYear: currentYear, nextSemester: currentSemester };
    }
    const [nextAcYear, nextSemester] = progression[currentIndex + 1];
    return { nextAcYear, nextSemester };
  };

  const getNextYearOnly = (currentYear) => {
    const yearProgression = { I: "II", II: "III", III: "IV", IV: "V", V: "VI" };
    return yearProgression[currentYear] || "";
  };

  const updateNextAcademicFields = (presentAcYear, presentSemester, isPharmaCourse) => {
    if (!presentAcYear) {
      studentdetailsformik.setFieldValue("nextAcYear", "");
      studentdetailsformik.setFieldValue("nextSemester", "");
      return;
    }

    if (isPharmaCourse) {
      studentdetailsformik.setFieldValue("presentSemester", "");
      studentdetailsformik.setFieldValue("nextAcYear", getNextYearOnly(presentAcYear));
      studentdetailsformik.setFieldValue("nextSemester", "");
      return;
    }

    if (!presentSemester) {
      studentdetailsformik.setFieldValue("nextAcYear", "");
      studentdetailsformik.setFieldValue("nextSemester", "");
      return;
    }

    const { nextAcYear, nextSemester } = getNextAcademicProgress(presentAcYear, presentSemester);
    studentdetailsformik.setFieldValue("nextAcYear", nextAcYear);
    studentdetailsformik.setFieldValue("nextSemester", nextSemester);
  };

  const YEAR_TO_ROMAN = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI" };

  const normalizeAcademicYear = (value) => {
    const raw = String(value ?? "").trim().toUpperCase();
    if (!raw) return "";
    if (["I", "II", "III", "IV", "V", "VI"].includes(raw)) return raw;

    const numeric = Number((raw.match(/^YEAR[_\s-]*(\d+)$/) || [])[1] || raw);
    return YEAR_TO_ROMAN[numeric] || "";
  };

  const normalizeSemester = (value) => {
    const raw = String(value ?? "").trim().toUpperCase().replace(/_/g, "-");
    if (!raw) return "";
    if (raw === "SEM-1" || raw === "1") return "SEM-1";
    if (raw === "SEM-2" || raw === "2") return "SEM-2";
    return "";
  };

  const normalizeYearNumber = (value) => {
    const raw = String(value ?? "").trim();
    const match = raw.match(/\d{4}/);
    return match ? Number(match[0]) : Number.NaN;
  };

  const getPresentAutonomousEligibility = (fromType, yearOfAdmission, autonomousAcademicYear, isLoadingStatus) => {
    if (!isAutonomousType(fromType)) {
      return { isEligible: true, isPending: false, message: "" };
    }

    if (isLoadingStatus) {
      return {
        isEligible: false,
        isPending: true,
        message: "Checking autonomous college eligibility...",
      };
    }

    const admissionYear = normalizeYearNumber(yearOfAdmission);
    const autonomousYear = normalizeYearNumber(autonomousAcademicYear);

    if (!Number.isFinite(admissionYear) || !Number.isFinite(autonomousYear)) {
      return {
        isEligible: false,
        isPending: false,
        message: "Autonomous college transfer is allowed only when Year of Admission is less than or equal to the college autonomous academic year.",
      };
    }

    if (admissionYear <= autonomousYear) {
      return { isEligible: true, isPending: false, message: "" };
    }

    return {
      isEligible: false,
      isPending: false,
      message: `Autonomous college transfer is allowed only when Year of Admission (${yearOfAdmission}) is less than or equal to the college autonomous academic year (${autonomousAcademicYear}).`,
    };
  };

  const setInstitutionTypeFromCollege = (collCode, collegeList, fieldName) => {
    const autoType = collCode ? getCollegeAutonomyType(collCode, collegeList) : "";
    studentdetailsformik.setFieldValue(fieldName, autoType || "");
  };

  const applyStudentProfileToForm = (item) => {
    const presentAcYear = normalizeAcademicYear(item?.present_ac_year ?? item?.course_year);
    const presentSemester = normalizeSemester(item?.present_semester ?? item?.course_sem);
    const { nextAcYear: computedNextAcYear, nextSemester: computedNextSemester } = getNextAcademicProgress(presentAcYear, presentSemester);
    const normalizedNextAcYear = normalizeAcademicYear(item?.next_ac_year ?? item?.next_course_year);
    const normalizedNextSemester = normalizeSemester(item?.next_semester ?? item?.next_course_sem);
    const nextAcYear = normalizedNextAcYear || computedNextAcYear || presentAcYear;
    const nextSemester = normalizedNextSemester || computedNextSemester || presentSemester;

    const formData = {
      ...initialValues,
      // studentRegNo: String(item?.student_reg_no || item?.stu_id || ""),
      studentName: item?.student_name || item?.stu_name || "",
      fatherName: item?.father_name || item?.fname || "",
      motherName: item?.mother_name || item?.mname || "",
      dob: item?.dob || "",
      gender: normalizeGender(gender || item?.gender),
      email: email || item?.email || item?.stu_email || "",
      studentRollNo: String(item?.student_roll_no || item?.roll_no || ""),
      presentInstType: String(item?.present_inst_type || ""),
      transInstType: String(item?.trans_inst_type || ""),
      nonAutonomousAtJoining: item?.non_autonomous_at_joining === true || item?.non_autonomous_at_joining === "Y",
      yearOfAdmission: String(item?.year_of_admission || item?.ssc_pass_year || ""),
      ubCode: String(item?.ub_code || ""),
      presentDistCode: String(item?.present_dist_code || item?.dist_code || item?.coll_dist || ""),
      presentCollCode: String(item?.present_coll_code || item?.coll_code || ""),
      courseId: String(item?.course_cat_id || item?.course_id || ""),
      branch: String(item?.course_code || item?.branch || ""),
      presentAcYear,
      presentSemester,
      lateralEntry: item?.lateral_entry === true || item?.lateral_entry === "Y",
      mobileNo: mobileNo || String(item?.mobile_no || item?.stu_mobileno || ""),
      transDistCode: String(item?.trans_dist_code || ""),
      transCollCode: String(item?.trans_coll_code || ""),
      transferReason: item?.transfer_reason || "",
      nextAcYear,
      nextSemester,
      sameSyllabus: item?.same_syllabus || "",
      currentSyllabusCopy: item?.current_syllabus_copy || "",
    };

    const profileCourseName = item?.course_cat_name || item?.course_name || "";
    setIsPharmaD(isPharmaDName(profileCourseName));

    setStudentDetails(item);
    studentdetailsformik.setValues(formData);

    if (formData.ubCode) {
      getDistrictsByUniversityCode(formData.ubCode);
      getColleges(formData.ubCode, formData.presentDistCode, setColleges, "present", formData.presentCollCode, "presentInstType");
      getColleges(formData.ubCode, formData.transDistCode, setTransferColleges, "transfer", formData.transCollCode, "transInstType");
      if (formData.presentCollCode) getCourses(formData.presentCollCode, formData.courseId);
      if (formData.presentCollCode) getCollegeStatus(formData.presentCollCode);
      if (formData.transCollCode) getTransferCollegeCourses(formData.transCollCode);
    }
  };

  const mapCollegeOption = (college) => {
    const value = String(college?.coll_code || college?.college_code || college?.inst_code || "");
    const label = college?.inst_name || college?.coll_name || college?.college_name || "";
    return { value, label };
  };

  const getAutonomyTypeFromName = (name) => {
    const collegeName = String(name || "").trim();
    if (!collegeName) return "";

    // Business rule: suffix "- N" means Non-Autonomous, "-A" means Autonomous.
    if (/-\s*N$/i.test(collegeName)) return "Non-Autonomous";
    if (/-\s*A$/i.test(collegeName)) return "Autonomous";
    return "";
  };

  const getCollegeAutonomyType = (collegeCode, collegeList) => {
    if (!collegeCode || !collegeList?.length) return "";
    const college = collegeList.find(
      (item) =>
        String(item?.coll_code || item?.college_code || item?.inst_code || "") === String(collegeCode)
    );
    if (!college) return "";

    const type = college?.automus_nonautomus || college?.autonomous_nonautonomous || "";
    const lowerType = type.toLowerCase();

    if (lowerType.includes("non")) return "Non-Autonomous";
    if (lowerType.includes("auto")) return "Autonomous";

    const collegeName = college?.inst_name || college?.coll_name || college?.college_name || "";
    const typeFromName = getAutonomyTypeFromName(collegeName);
    if (typeFromName) return typeFromName;

    return type;
  };

  const fetchCollegeCourses = async (collCode) => {
    if (!collCode) return [];
    try {
      const url = `${config.url.STU_URL}getcollegecoursres/${collCode}`;
      const res = await CommonAxiosGet(url);
      const courseList = res?.data?.colleges || [];
      return res?.data?.scode === "01" && Array.isArray(courseList) ? courseList : [];
    } catch {
      return [];
    }
  };

  const setCollegeLoading = (type, value) => {
    setIsLoadingColleges((prev) => ({ ...prev, [type]: value }));
  };

  const getColleges = async (ubCode, distCode, setter, type, selectedCollegeCode = "", instTypeField = "") => {
    if (!ubCode || !distCode) {
      setter([]);
      return;
    }

    if (type) setCollegeLoading(type, true);
    try {
      const url = `${config.url.STU_URL}getcollegedetails/${ubCode}/${distCode}`;
      const res = await CommonAxiosGet(url);
      const collegeList = res?.data?.districtmst || [];
      const finalCollegeList = res?.data?.scode === "01" && Array.isArray(collegeList) ? collegeList : [];
      setter(finalCollegeList);

      // Auto-populate institution type when selected college code is already known.
      if (selectedCollegeCode && instTypeField) {
        const autoType = getCollegeAutonomyType(selectedCollegeCode, finalCollegeList);
        if (autoType) studentdetailsformik.setFieldValue(instTypeField, autoType);
      }
    } catch {
      setter([]);
    } finally {
      if (type) setCollegeLoading(type, false);
    }
  };

  const getCourses = async (collCode, selectedCourseId = "") => {
    if (!collCode) {
      setCourses([]);
      setBranches([]);
      return;
    }

    setIsLoadingCourses(true);
    try {
      const finalCourseList = await fetchCollegeCourses(collCode);
      setCourses(finalCourseList);

      if (selectedCourseId) {
        const filtered = finalCourseList.filter((course) => String(course.course_cat_id) === String(selectedCourseId));
        setBranches(filtered);
        const selectedCourse = finalCourseList.find((course) => String(course.course_cat_id) === String(selectedCourseId));
        setIsPharmaD(isPharmaDName(selectedCourse?.course_cat_name || selectedCourse?.course_name));
      } else {
        setBranches([]);
        setIsPharmaD(false);
      }
    } catch {
      setCourses([]);
      setBranches([]);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const getTransferCollegeCourses = async (collCode) => {
    if (!collCode) {
      setTransferCollegeCourses([]);
      return;
    }
    setIsLoadingTransferCourses(true);
    try {
      const finalCourseList = await fetchCollegeCourses(collCode);
      setTransferCollegeCourses(finalCourseList);
    } finally {
      setIsLoadingTransferCourses(false);
    }
  };

  const filterBranches = (courseCategoryId) => {
    if (!courseCategoryId || courses.length === 0) {
      setBranches([]);
      return;
    }

    const filtered = courses.filter((course) => String(course.course_cat_id) === String(courseCategoryId));
    setBranches(filtered);
  };

  const getUniqueCourses = (courseList) => {
    const uniqueMap = new Map();

    courseList.forEach((course) => {
      const code = String(course?.course_cat_id || "");
      if (!code || uniqueMap.has(code)) return;
      uniqueMap.set(code, course);
    });

    return Array.from(uniqueMap.values());
  };

  function getDistrictsByUniversityCode(ubCode) {
    if (!ubCode) {
      setDistricts([]);
      return;
    }
    setIsLoadingDistricts(true);
    const url = `${config.url.STU_URL}getdistrictsbyubcode/${ubCode}`;
    CommonAxiosGet(url)
      .then((res) => {
        if (res?.data?.scode === "01") {
          const districtList = res?.data?.districtmst || [];
          setDistricts(Array.isArray(districtList) ? districtList : []);
        } else {
          setDistricts([]);
        }
      })
      .catch(() => {
        setDistricts([]);
      })
      .finally(() => {
        setIsLoadingDistricts(false);
      });
  }

  async function getUniversityDetails() {
    try {
      const url = config.url.STU_URL + "getuniversitydetails";
      const res = await CommonAxiosGet(url);
      if (res?.data?.scode === "01") {
        setUniversities(res?.data?.districtmst || []);
      } else {
        setUniversities([]);
      }
    } catch {
      setUniversities([]);
    }
  }

  async function getStudentDetails() {
    try {
      const url = config.url.STU_URL + "getstudentdetails";
      const res = await CommonAxiosGet(url);
      if (res?.data?.scode === "01" && res?.data?.districtmst?.length > 0) {
        const item = res.data.districtmst[0];
        applyStudentProfileToForm(item);
        return true;
      }

      setStudentDetails({});
      studentdetailsformik.setValues(initialValues);
      setDistricts([]);
      setColleges([]);
      setTransferColleges([]);
      setTransferCollegeCourses([]);
      setCourses([]);
      setBranches([]);
      return false;
    } catch {
      setStudentDetails({});
      setDistricts([]);
      setColleges([]);
      setTransferColleges([]);
      setTransferCollegeCourses([]);
      return false;
    }
  }

  async function getStudentProfile() {
    let StudentId = isStudent ? userId : studentRegNo;
    const url = config.url.STU_URL + "getStudentProfile/" + StudentId;
    try {
      const res = await CommonAxiosGet(url);
      const profileList = res?.data?.data || [];

      if (res?.data?.scode === "01" && Array.isArray(profileList) && profileList.length > 0) {
        applyStudentProfileToForm(profileList[0]);
        return true;
      }
    } catch {
      // Keep existing values if profile refresh fails after submit.
    }

    return false;
  }

  async function getCollegeStatus(collCode) {
    if (!collCode) {
      setPresentCollegeStatus(null);
      return;
    }
    setIsLoadingPresentCollegeStatus(true);
    try {
      const url = `${config.url.STU_URL}getcollegestatus/${collCode}`;
      const res = await CommonAxiosGet(url);
      if (res?.data?.scode === "01") {
        setPresentCollegeStatus(res?.data?.data?.[0] || null);
      } else { setPresentCollegeStatus(null); }
    } catch {
      setPresentCollegeStatus(null);
    } finally { setIsLoadingPresentCollegeStatus(false); }
  }

  useEffect(() => {
    const initializeStudentData = async () => {
      await getUniversityDetails();
      const hasProfileData = await getStudentProfile();
      if (!hasProfileData) {
        await getStudentDetails();
      }
    };

    initializeStudentData();
  }, []);

  const backendProfileData = getstudentDtls && !Array.isArray(getstudentDtls) ? getstudentDtls : {};
  const hasBackendValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== "";
  const hasBackendFieldValue = (...keys) => keys.some((key) => Object.prototype.hasOwnProperty.call(backendProfileData, key) && hasBackendValue(backendProfileData[key]));

  const isStudentRegNoLocked = Boolean(userId) || hasBackendFieldValue("student_reg_no", "stu_id");
  const isStudentNameLocked = Boolean(username) || hasBackendFieldValue("student_name", "stu_name");
  const isFatherNameLocked = hasBackendFieldValue("father_name", "fname");
  const isMotherNameLocked = hasBackendFieldValue("mother_name", "mname");
  const isDobLocked = hasBackendFieldValue("dob");
  const isGenderLocked = Boolean(gender) || hasBackendFieldValue("gender");
  const isEmailLocked = Boolean(email) || hasBackendFieldValue("email", "stu_email");
  const isMobileNoLocked = Boolean(mobileNo) || hasBackendFieldValue("mobile_no", "stu_mobileno");
  const isStudentRollNoLocked = hasBackendFieldValue("student_roll_no", "roll_no");
  const isPresentInstTypeLocked = hasBackendFieldValue("present_inst_type") || Boolean(studentdetailsformik.values.presentInstType);
  const isTransInstTypeLocked = hasBackendFieldValue("trans_inst_type") || Boolean(studentdetailsformik.values.transInstType);

  const isYearOfAdmissionLocked = hasBackendFieldValue("year_of_admission", "ssc_pass_year");
  const isUniversityLocked = hasBackendFieldValue("ub_code");
  const isPresentDistLocked = hasBackendFieldValue("present_dist_code", "dist_code", "coll_dist");
  const isPresentCollegeLocked = hasBackendFieldValue("present_coll_code", "coll_code");
  const isCourseLocked = hasBackendFieldValue("course_cat_id", "course_id");
  const isBranchLocked = hasBackendFieldValue("course_code", "branch") && hasBackendFieldValue("course_cat_id", "course_id");
  const isPresentAcademicYearLocked = hasBackendFieldValue("present_ac_year", "course_year");
  const isPresentSemesterLocked = hasBackendFieldValue("present_semester", "course_sem");
  const issameSyllabusLocked = hasBackendFieldValue("same_syllabus");
  const isCurrentSyllabusCopyLocked = hasBackendFieldValue("current_syllabus_copy");

  const isNextAcademicYearLocked = true;
  const isNextSemesterLocked = true;

  // Transfer eligibility logic
  const selectedPresentCollCode = studentdetailsformik.values.presentCollCode;
  const selectedTransCollCode = studentdetailsformik.values.transCollCode;
  const presentAutoType = getCollegeAutonomyType(selectedPresentCollCode, colleges) || studentdetailsformik.values.presentInstType;
  const transferAutoType = getCollegeAutonomyType(selectedTransCollCode, transferColleges) || studentdetailsformik.values.transInstType;
  const presentAutonomousEligibility = getPresentAutonomousEligibility(
    presentAutoType,
    studentdetailsformik.values.yearOfAdmission,
    presentCollegeStatus?.ac_year,
    isLoadingPresentCollegeStatus
  );
  const transferRuleStatus = getTransferRuleStatus(presentAutoType, transferAutoType, presentAutonomousEligibility);
  const isPresentAutonomous = isAutonomousType(presentAutoType);
  const isPresentAutonomousBlocked = isPresentAutonomous && !presentAutonomousEligibility.isEligible;
  const showNonAutoToAutoMessage = presentAutoType === "Non-Autonomous" && transferAutoType === "Autonomous";

  // Placeholder messages
  const getPresentCollPlaceholder = () => {
    if (!studentdetailsformik.values.ubCode || !studentdetailsformik.values.presentDistCode) return "Select district first";
    if (isLoadingColleges.present) return "Loading institutions...";
    return colleges.length > 0 ? "Select Institution" : "No institutions found";
  };

  const getTransferCollPlaceholder = () => {
    if (!studentdetailsformik.values.ubCode || !studentdetailsformik.values.transDistCode) return "Select district first";
    if (isLoadingColleges.transfer) return "Loading institutions...";
    return transferColleges.length > 0 ? "Select Institution" : "No institutions found";
  };

  const getDistrictPlaceholder = () => {
    if (!studentdetailsformik.values.ubCode) return "Select affiliated university first";
    if (isLoadingDistricts) return "Loading districts...";
    return districts.length > 0 ? "Select District" : "No districts found for selected university";
  };

  const handleUniversityChange = (event) => {
    const ubCode = event.target.value;
    studentdetailsformik.setValues({
      ...studentdetailsformik.values,
      ubCode,
      presentDistCode: "",
      transDistCode: "",
      presentCollCode: "",
      transCollCode: "",
      presentInstType: "",
      transInstType: "",
      courseId: "",
      branch: "",
    });
    setDistricts([]);
    setColleges([]);
    setTransferColleges([]);
    setTransferCollegeCourses([]);
    setCourses([]);
    setBranches([]);
    setPresentCollegeStatus(null);

    if (ubCode) {
      getDistrictsByUniversityCode(ubCode);
    }
  };

  const handleDistrictChange = (event, type) => {
    const distCode = event.target.value;
    const ubCode = studentdetailsformik.values.ubCode;

    if (type === "present") {
      studentdetailsformik.setFieldValue("presentDistCode", distCode);
      studentdetailsformik.setFieldValue("presentCollCode", "");
      studentdetailsformik.setFieldValue("presentInstType", "");
      studentdetailsformik.setFieldValue("courseId", "");
      studentdetailsformik.setFieldValue("branch", "");
      setCourses([]);
      setBranches([]);
      setPresentCollegeStatus(null);
      getColleges(ubCode, distCode, setColleges, "present");
    } else {
      studentdetailsformik.setFieldValue("transDistCode", distCode);
      studentdetailsformik.setFieldValue("transCollCode", "");
      studentdetailsformik.setFieldValue("transInstType", "");
      setTransferCollegeCourses([]);
      getColleges(ubCode, distCode, setTransferColleges, "transfer");
    }
  };

  const handlePresentCollegeChange = (event) => {
    const collCode = event.target.value;
    studentdetailsformik.setFieldValue("presentCollCode", collCode);
    studentdetailsformik.setFieldValue("courseId", "");
    studentdetailsformik.setFieldValue("branch", "");

    setInstitutionTypeFromCollege(collCode, colleges, "presentInstType");

    setBranches([]);
    getCollegeStatus(collCode);
    getCourses(collCode);
  };

  const handleTransferCollegeChange = (event) => {
    const collCode = event.target.value;
    const autoType = collCode ? getCollegeAutonomyType(collCode, transferColleges) : "";

    studentdetailsformik.setFieldValue("transCollCode", collCode, true);
    studentdetailsformik.setFieldTouched("transCollCode", true, false);
    studentdetailsformik.setFieldValue("transInstType", autoType || "");
    getTransferCollegeCourses(collCode);
  };

  const handleCourseChange = (event) => {
    const courseCategoryId = event.target.value;
    studentdetailsformik.setFieldValue("courseId", courseCategoryId);
    studentdetailsformik.setFieldValue("branch", "");
    filterBranches(courseCategoryId);
    const selectedCourse = uniqueCourses.find(
      (course) => String(course.course_cat_id) === String(courseCategoryId)
    );
    const isSelectedPharmaD = selectedCourse?.course_cat_name === "Pharma.D";
    setIsPharmaD(isSelectedPharmaD);

    updateNextAcademicFields(
      studentdetailsformik.values.presentAcYear,
      studentdetailsformik.values.presentSemester,
      isSelectedPharmaD
    );
  };

  const collegeOptions = colleges.map(mapCollegeOption).filter((c) => c.value && c.label);
  const transferCollegeOptions = transferColleges.map(mapCollegeOption).filter((c) => c.value && c.label);

  // Add fallback for selected college if not in list
  const presentCollegeOptions = collegeOptions.some((c) => c.value === selectedPresentCollCode)
    ? collegeOptions
    : selectedPresentCollCode
      ? [{ value: selectedPresentCollCode, label: selectedPresentCollCode }, ...collegeOptions]
      : collegeOptions;

  const finalTransferCollegeOptions = transferCollegeOptions.some((c) => c.value === selectedTransCollCode)
    ? transferCollegeOptions
    : selectedTransCollCode
      ? [{ value: selectedTransCollCode, label: selectedTransCollCode }, ...transferCollegeOptions]
      : transferCollegeOptions;

  const uniqueCourses = getUniqueCourses(courses);
  const selectedBranchValue = studentdetailsformik.values.branch;
  const branchOptions = branches.some((b) => String(b.course_code) === String(selectedBranchValue))
    ? branches
    : selectedBranchValue
      ? [{ course_code: selectedBranchValue, course_name: selectedBranchValue }, ...branches]
      : branches;

  const getCourseNameById = (courseList, courseId) => {
    const matchingCourse = courseList.find((course) => String(course?.course_cat_id) === String(courseId));
    return matchingCourse?.course_cat_name || "";
  };

  const getBranchNameByCode = (courseList, branchCode) => {
    const matchingBranch = courseList.find((course) => String(course?.course_code) === String(branchCode));
    return matchingBranch?.course_name || "";
  };

  const transferCourseAvailability = (() => {
    if (!selectedTransCollCode || !studentdetailsformik.values.courseId || !studentdetailsformik.values.branch || isLoadingTransferCourses) {
      return { isAvailable: true, message: "" };
    }

    const matchingCourseList = transferCollegeCourses?.filter(
      (course) => String(course?.course_cat_id) === String(studentdetailsformik?.values.courseId)
    );

    const selectedCourseName = getCourseNameById(uniqueCourses, studentdetailsformik.values.courseId);
    const selectedBranchName = getBranchNameByCode(branchOptions, studentdetailsformik.values.branch);

    if (matchingCourseList.length === 0) {
      return {
        isAvailable: false,
        message: `Present course${selectedCourseName ? ` (${selectedCourseName})` : ""} is not available in the transfer college.`,
      };
    }

    const hasMatchingBranch = matchingCourseList.some(
      (course) => String(course?.course_code) === String(studentdetailsformik.values.branch)
    );

    if (!hasMatchingBranch) {
      return {
        isAvailable: false,
        message: `Present branch${selectedBranchName ? ` (${selectedBranchName})` : ""} is not available in the transfer college.`,
      };
    }
    return { isAvailable: true, message: "" };
  })();
  // const baseAdmissionYears = ["2022", "2023", "2024", "2025", "2026"];
  const currentYear = new Date().getFullYear();

  const baseAdmissionYears = Array.from({ length: 6 }, (_, index) => String(currentYear - 6 + index));
  const selectedAdmissionYear = String(studentdetailsformik.values.yearOfAdmission || "");
  const admissionYearOptions = selectedAdmissionYear && !baseAdmissionYears.includes(selectedAdmissionYear) ? [selectedAdmissionYear, ...baseAdmissionYears] : baseAdmissionYears;

  const handlePresentAcademicYearChange = (event) => {
    const presentAcYear = event.target.value;
    const presentSemester = studentdetailsformik.values.presentSemester;

    // Clear next year and semester if no year selected
    if (!presentAcYear) {
      studentdetailsformik.setFieldValue("presentAcYear", "");
      studentdetailsformik.setFieldValue("nextAcYear", "");
      studentdetailsformik.setFieldValue("nextSemester", "");
      return;
    }
    studentdetailsformik.setFieldValue("presentAcYear", presentAcYear);
    updateNextAcademicFields(presentAcYear, presentSemester, isPharmaD);
  };

  const handlePresentSemesterChange = (event) => {
    const presentSemester = event.target.value;
    const presentAcYear = studentdetailsformik.values.presentAcYear;

    // Clear next year and semester if no semester selected
    if (!presentSemester) {
      studentdetailsformik.setFieldValue("presentSemester", "");
      studentdetailsformik.setFieldValue("nextAcYear", "");
      studentdetailsformik.setFieldValue("nextSemester", "");
      return;
    }

    const { nextAcYear, nextSemester } = getNextAcademicProgress(presentAcYear, presentSemester);

    studentdetailsformik.setFieldValue("presentSemester", presentSemester);
    studentdetailsformik.setFieldValue("nextAcYear", nextAcYear);
    studentdetailsformik.setFieldValue("nextSemester", nextSemester);
  };

  const presentSemesterOptions = (() => {
    const presentAcYear = studentdetailsformik.values.presentAcYear;
    const isLateralEntry = studentdetailsformik.values.lateralEntry;

    if (!presentAcYear) return [];
    if (presentAcYear === "I") return [{ value: "SEM-2", label: "SEM 2" }];
    if (presentAcYear === "IV") return [{ value: "SEM-1", label: "SEM 1" }];
    if (isLateralEntry && presentAcYear === "II") return [{ value: "SEM-2", label: "SEM 2" }];

    return [
      { value: "SEM-1", label: "SEM 1" },
      { value: "SEM-2", label: "SEM 2" },
    ];
  })();

  return (
    <jnb.Container fluid className="st-form-page py-3 py-lg-4">
      <div className="st-form-shell">
        {isStudent && (
          <div className="st-form-title-wrap">
            <h4 className="st-form-title">Student Transfer Application</h4>
            <p className="st-form-subtitle">Fill in the details below to apply for student transfer</p>
          </div>
        )}
        <FormikProvider value={studentdetailsformik}>
          <Form onSubmit={studentdetailsformik?.handleSubmit} onChange={studentdetailsformik?.handleChange}>
            <section className="st-form-card">
              <div className="st-form-card-header">
                <span className="st-form-card-index">1</span>
                <h5>Basic Details</h5>
              </div>
              <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Student Registration Number <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="studentRegNo" className="form-control st-control" maxLength={20} onKeyPress={allowNumbersAndAlphabetsOnly} disabled={isStudentRegNoLocked} />
                  <ErrorMessage name="studentRegNo" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Name of the Student <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="studentName" className="form-control st-control" maxLength={100} onKeyPress={allowAlphabetAndSpacesOnly} disabled={isStudentNameLocked} />
                  <ErrorMessage name="studentName" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Father's Name <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="fatherName" className="form-control st-control" maxLength={100} onKeyPress={allowAlphabetAndSpacesOnly} disabled={paymentFlag} />
                  <ErrorMessage name="fatherName" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Mother's Name <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="motherName" className="form-control st-control" maxLength={100} onKeyPress={allowAlphabetAndSpacesOnly} disabled={paymentFlag} />
                  <ErrorMessage name="motherName" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Date of Birth <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="dob" type="date" className="form-control st-control" max={getMaxDate()} disabled={paymentFlag} />
                  <ErrorMessage name="dob" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Gender <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field as="select" name="gender" className="form-select st-control" disabled={paymentFlag}>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Field>
                  <ErrorMessage name="gender" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Email <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="email" type="email" className="form-control st-control" maxLength={100} disabled={paymentFlag} />
                  <ErrorMessage name="email" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Mobile Number <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="mobileNo" className="form-control st-control" maxLength={10} onKeyPress={allowNumbersOnly} disabled={paymentFlag} />
                  <ErrorMessage name="mobileNo" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Student Roll No <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field name="studentRollNo" className="form-control st-control" maxLength={20} onKeyPress={allowNumbersAndAlphabetsOnly} disabled={paymentFlag} />
                  <ErrorMessage name="studentRollNo" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <div className="d-flex align-items-center  gap-4">
                  <jnb.Form.Label className="st-label mb-0">
                    Is this same syllabus? <span className="text-danger">*</span>
                  </jnb.Form.Label>

                  <div className="d-flex align-items-center gap-1">
                    <Field type="radio" name="sameSyllabus" value="Yes" className="form-check-input m-0" disabled={paymentFlag} />
                    <span style={{ fontWeight: 500 }}>Yes</span>
                  </div>

                  <div className="d-flex align-items-center gap-1">
                    <Field type="radio" name="sameSyllabus" value="No" className="form-check-input m-0" disabled={paymentFlag}
                      onChange={() => studentdetailsformik.setFieldValue("currentSyllabusCopy", "")} />
                    <span style={{ fontWeight: 500 }}>No</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "8px 16px",
                      background: "#f8fbff",
                      border: "1px solid #cfe2ff",
                      borderRadius: "25px",
                      fontSize: "0.9rem",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#0d6efd" }}>📌 Note:</span>

                    <span style={{ color: "#198754" }}>✅  Yes → Syllabus matches in both colleges. Upload syllabus copy.</span>

                    <span style={{ color: "#adb5bd" }}>|</span>

                    <span style={{ color: "#dc3545" }}>❌ No → No upload required</span>
                  </div>
                </div>
                <ErrorMessage name="sameSyllabus" component="div" className="text-danger extra-small mt-1" />

                {studentdetailsformik.values.sameSyllabus === "Yes" && (
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Upload Syllabus Copy <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="file"
                        name="currentSyllabusCopy"
                        className="form-control st-control"
                        accept=".pdf"
                        onChange={(event) => {
                          const filename = "currentSyllabusCopy";
                          const path = "/Uploads/StudentTransfer/";
                          ImagePdfBucket(event, studentdetailsformik, path, "currentSyllabusCopy", filename, "pdf").then((uploadedPath) => {
                            studentdetailsformik.setFieldValue("currentSyllabusCopy", uploadedPath || "");
                          });
                        }}
                        disabled={paymentFlag}
                      />
                      {studentdetailsformik.values.currentSyllabusCopy && (
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => viewImage(studentdetailsformik.values.currentSyllabusCopy)}>
                          <FaEye />
                        </button>
                      )}
                    </div>
                    <ErrorMessage name="currentSyllabusCopy" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                )}
                <jnb.Col xs={12}>
                  <jnb.Form.Check className="mt-1">
                    <Field type="checkbox" name="lateralEntry" id="lateralEntry" disabled={paymentFlag}
                      onChange={() => {
                        const isChecked = !studentdetailsformik.values.lateralEntry;
                        studentdetailsformik.setFieldValue("lateralEntry", isChecked);
                        studentdetailsformik.setFieldValue("presentAcYear", "");
                        studentdetailsformik.setFieldValue("presentSemester", "");
                      }}
                    />
                    <jnb.Form.Label htmlFor="lateralEntry" className="small fw-bold text-dark ms-2">
                      Check this box if you are admitted through Lateral Entry
                    </jnb.Form.Label>
                  </jnb.Form.Check>
                  {/* <ErrorMessage name="lateralEntry" component="div" className="text-danger extra-small mt-1" /> */}
                </jnb.Col>
              </jnb.Row>
            </section>
            <section className="st-form-card">
              <div className="st-form-card-header">
                <span className="st-form-card-index">2</span>
                <h5>Current Academic Details (From College)</h5>
              </div>
              <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Year of Admission <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field as="select" name="yearOfAdmission" className="form-select st-control" disabled={paymentFlag}>
                    <option value="">Select Year</option>
                    {admissionYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="yearOfAdmission" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Affiliated University <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field as="select" name="ubCode" className="form-select st-control" onChange={handleUniversityChange} onBlur={studentdetailsformik.handleBlur}
                    disabled={paymentFlag}>
                    <option value="">Select University</option>
                    {universities?.length > 0 ? (
                      universities.map((university) => (
                        <option key={university.ub_code} value={String(university.ub_code)}>
                          {university.ub_name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No universities available
                      </option>
                    )}
                  </Field>
                  <ErrorMessage name="ubCode" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Present Studying College District <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field
                    as="select"
                    name="presentDistCode"
                    className="form-select st-control"
                    onChange={(e) => handleDistrictChange(e, "present")}
                    onBlur={studentdetailsformik.handleBlur}
                    // disabled={isPresentDistLocked || !studentdetailsformik.values.ubCode || isLoadingDistricts}
                    disabled={paymentFlag}
                  >
                    <option value="">{getDistrictPlaceholder()}</option>
                    {districts?.map((district) => (
                      <option key={district.dist_code} value={district.dist_code}>
                        {district.dist_name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="presentDistCode" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Present Studying Institution <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field
                    as="select"
                    name="presentCollCode"
                    className="form-select st-control"
                    onChange={handlePresentCollegeChange}
                    // disabled={isPresentCollegeLocked || !studentdetailsformik.values.presentDistCode || isLoadingColleges.present}
                    disabled={paymentFlag}
                  >
                    <option value="">{getPresentCollPlaceholder()}</option>
                    {presentCollegeOptions.map((college) => (
                      <option key={college.value} value={college.value}>
                        {college.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="presentCollCode" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                {selectedPresentCollCode && (
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Institution Type <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field as="select" name="presentInstType" className="form-select st-control" disabled={isPresentInstTypeLocked}>
                      <option value="">Select Institution Type</option>
                      <option value="Autonomous">Autonomous</option>
                      <option value="Non-Autonomous">Non-Autonomous</option>
                    </Field>
                    <ErrorMessage name="presentInstType" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                )}

                {/* {!showPresentWarning && ( */}
                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Course <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field
                    as="select"
                    name="courseId"
                    className="form-select st-control"
                    onChange={handleCourseChange}
                    // disabled={isCourseLocked || !studentdetailsformik.values.presentCollCode || isLoadingCourses}
                    disabled={paymentFlag}
                  >
                    <option value="">
                      {!studentdetailsformik.values.presentCollCode ? "Select institution first" : isLoadingCourses ? "Loading courses..." : courses.length > 0 ? "Select Course" : "No courses found"}
                    </option>
                    {uniqueCourses.map((course) => (
                      <option key={course.course_cat_id} value={course.course_cat_id}>
                        {course.course_cat_name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="courseId" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>
                {/* )} */}

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Branch <span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field as="select" name="branch" className="form-select st-control"
                    // disabled={isBranchLocked || !studentdetailsformik.values.courseId || branches.length === 0}
                    disabled={paymentFlag}
                  >
                    <option value="">{!studentdetailsformik.values.courseId ? "Select course first" : branches.length > 0 ? "Select Branch" : "No branches found"}</option>
                    {branchOptions.map((branch) => (
                      <option key={branch.course_code} value={branch.course_code}>
                        {branch.course_name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="branch" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>

                <jnb.Col xs={12} md={6} lg={4}>
                  <jnb.Form.Label className="st-label">
                    Present Year<span className="text-danger">*</span>
                  </jnb.Form.Label>
                  <Field
                    as="select"
                    name="presentAcYear"
                    className="form-select st-control"
                    onChange={handlePresentAcademicYearChange}
                    onBlur={studentdetailsformik.handleBlur}
                    // disabled={isPresentAcademicYearLocked}
                    disabled={paymentFlag}
                  >
                    <option value="">Select Year</option>

                    {!studentdetailsformik.values.lateralEntry && <option value="I">I Year</option>}
                    <option value="II">II Year</option>
                    <option value="III">III Year</option>
                    <option value="IV">IV Year</option>
                    {isPharmaD && <option value="V">V Year</option>}
                  </Field>
                  <ErrorMessage name="presentAcYear" component="div" className="text-danger extra-small mt-1" />
                </jnb.Col>
                {isPharmaD === false && (
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Present Semester <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field
                      as="select"
                      name="presentSemester"
                      className="form-select st-control"
                      onChange={handlePresentSemesterChange}
                      onBlur={studentdetailsformik.handleBlur}
                      // disabled={isPresentSemesterLocked}
                      disabled={paymentFlag}
                    >
                      <option value="">Select Semester</option>
                      {presentSemesterOptions.map((semester) => (
                        <option key={semester.value} value={semester.value}>
                          {semester.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="presentSemester" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>)}
              </jnb.Row>
            </section>

            {isPresentAutonomousBlocked && (
              <div className={`alert py-2 ${transferRuleStatus.isPending ? "alert-info" : "alert-danger"} mb-3`} role="alert">
                {transferRuleStatus.message}
              </div>
            )}

            {!isPresentAutonomousBlocked && (
              <section className="st-form-card">
                <div className="st-form-card-header">
                  <span className="st-form-card-index">3</span>
                  <h5>Transfer College Details (To College)</h5>
                </div>
                <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Transfer College District <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field
                      as="select"
                      name="transDistCode"
                      className="form-select st-control"
                      onChange={(e) => handleDistrictChange(e, "transfer")}
                      onBlur={studentdetailsformik.handleBlur}
                      // disabled={!studentdetailsformik.values.ubCode || isLoadingDistricts || paymentFlag}
                      disabled={paymentFlag}
                    >
                      <option value="">{getDistrictPlaceholder()}</option>
                      {districts?.map((district) => (
                        <option key={district.dist_code} value={district.dist_code}>
                          {district.dist_name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="transDistCode" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Transfer College Institution <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field
                      as="select"
                      name="transCollCode"
                      className="form-select st-control"
                      onChange={handleTransferCollegeChange}
                      onBlur={studentdetailsformik.handleBlur}
                      // disabled={!studentdetailsformik.values.ubCode || !studentdetailsformik.values.transDistCode || isLoadingColleges.transfer || paymentFlag}
                      disabled={paymentFlag}
                    >
                      <option value="">{getTransferCollPlaceholder()}</option>
                      {finalTransferCollegeOptions.map((college) => (
                        <option key={college.value} value={college.value}>
                          {college.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="transCollCode" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  {selectedTransCollCode && (
                    <jnb.Col xs={12} md={6} lg={4}>
                      <jnb.Form.Label className="st-label">
                        Institution Type <span className="text-danger">*</span>
                      </jnb.Form.Label>
                      <Field as="select" name="transInstType" className="form-select st-control" disabled={isTransInstTypeLocked}>
                        <option value="">Select Institution Type</option>
                        <option value="Autonomous">Autonomous</option>
                        <option value="Non-Autonomous">Non-Autonomous</option>
                      </Field>
                      <ErrorMessage name="transInstType" component="div" className="text-danger extra-small mt-1" />
                    </jnb.Col>
                  )}
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Year <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field as="select" name="nextAcYear" className="form-select st-control" onBlur={studentdetailsformik.handleBlur} disabled={isNextAcademicYearLocked}>
                      <option value="">Select Year</option>
                      {!studentdetailsformik.values.lateralEntry && <option value="II">II Year</option>}
                      <option value="III">III Year</option>
                      <option value="IV">IV Year</option>
                      {isPharmaD && <option value="V">V Year</option>}
                      {isPharmaD && <option value="VI">VI Year</option>}
                    </Field>
                    <ErrorMessage name="nextAcYear" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                  {isPharmaD === false && (
                    <jnb.Col xs={12} md={6} lg={4}>
                      <jnb.Form.Label className="st-label">
                        Next Semester <span className="text-danger">*</span>
                      </jnb.Form.Label>
                      <Field as="select" name="nextSemester" className="form-select st-control" onBlur={studentdetailsformik.handleBlur} disabled={isNextSemesterLocked}>
                        <option value="">Select Semester</option>
                        <option value="SEM-1">SEM 1</option>
                        <option value="SEM-2">SEM 2</option>
                      </Field>
                      <ErrorMessage name="nextSemester" component="div" className="text-danger extra-small mt-1" />
                    </jnb.Col>)}

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Reason For Transfer <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field as="select" name="transferReason" className="form-select st-control" disabled={paymentFlag}>
                      <option value="">Select reason</option>
                      <option value="Medical Grounds">Medical Grounds</option>
                      <option value="Transfer of Parents working in Govt./PSU/Corporation">Transfer of Parents working in Govt./PSU/Corporation</option>
                      <option value="Closure of course/Closure of college">Closure of course/Closure of college</option>
                    </Field>
                    <ErrorMessage name="transferReason" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  {showNonAutoToAutoMessage && (
                    <jnb.Col xs={12}>
                      <div className="alert py-2 alert-danger mb-0" role="alert">
                        {transferRuleStatus.message}
                      </div>
                    </jnb.Col>
                  )}

                  {!showNonAutoToAutoMessage && !transferCourseAvailability.isAvailable && (
                    <jnb.Col xs={12}>
                      <div className="alert py-2 alert-danger mb-0" role="alert">
                        {transferCourseAvailability.message}
                      </div>
                    </jnb.Col>
                  )}
                </jnb.Row>
              </section>
            )}

            {isStudent && (
              <div className="st-form-actions">
                {!paymentFlag && transferRuleStatus.isAllowed && transferCourseAvailability.isAvailable && !isPresentAutonomousBlocked ? (
                  <button type="submit" className="st-submit-btn">
                    Submit
                  </button>
                ) : !paymentFlag ? (
                  <div style={{ color: "#b02a37", fontWeight: "bold" }}>
                    Transfer not allowed for the selected institution type combination.
                  </div>
                ) : (
                  <div style={{ color: "green", fontWeight: "bold" }}>✅ Already submitted</div>
                )}
              </div>
            )}
          </Form>
        </FormikProvider>
      </div>
    </jnb.Container>
  );
}
